import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Info, ChevronDown, Monitor, Smartphone, X } from 'lucide-react';
import dbUrl from '../../utils/credentials';
import PageHeader from '../../components/layout/PageHeader';
import InvitationPreview from '../../components/invitation/InvitationPreview';
import InvitationCanvas from '../../components/invitation/InvitationCanvas';
import LocationPicker from '../../components/invitation/LocationPicker';
import ShareInvitationDialog from '../../components/invitation/ShareInvitationDialog';
import { ImageUpload, FieldBuilder } from '../../components/invitation/InvitationFormParts';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { RichTextEditor } from '../../components/ui/rich-text-editor';
import { DateTimePicker } from '../../components/ui/date-time-picker';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  getInitialFormValues,
  applyTemplate,
  resolveUniqueSlug,
  COLOR_PRESETS,
  THEME_LAYOUTS_META,
  BUTTON_STYLES,
  COVER_STYLES,
  FONT_SCALES,
  FONT_FAMILIES,
  INVITATION_TEMPLATES,
  normalizeInvitation,
  normalizeLocation,
  DEFAULT_THEME,
  DEFAULT_LOCATION,
  DEFAULT_SUCCESS_MESSAGE,
  DEFAULT_CLOSED_MESSAGE,
  ensureFieldsHaveIds,
  getInitialFieldValues,
  pruneHiddenFieldValues,
  normalizeDescriptionForSave,
  mergeFieldHiddenFlags,
} from '../../lib/invitation';
import { getResponseCount, stripResponsesFromInvitation } from '../../lib/responses';
import { toast } from '../../lib/toast';
import { cn } from '../../lib/utils';

const FIELDS_DISCLAIMER_STORAGE_KEY = 'ba-fields-disclaimer-open';

function LayoutChoiceCard({ option, selected, onSelect }) {
  const sketch = {
    classic: (
      <div className="flex h-16 flex-col items-center justify-center gap-1 px-3">
        <div className="h-2 w-8 rounded bg-brand-orange/40" />
        <div className="h-1.5 w-14 rounded bg-[var(--ba-muted)]/30" />
        <div className="mt-1 h-6 w-full rounded border border-[var(--ba-surface-border)] bg-[var(--ba-surface-bg)]" />
      </div>
    ),
    card: (
      <div className="flex h-16 items-center justify-center px-3">
        <div className="flex h-12 w-full flex-col items-center justify-center gap-1 rounded-lg border-2 border-brand-green/40 bg-[var(--ba-surface-bg)] px-2 shadow-sm">
          <div className="h-1.5 w-8 rounded bg-brand-orange/50" />
          <div className="h-1 w-12 rounded bg-[var(--ba-muted)]/25" />
        </div>
      </div>
    ),
    minimal: (
      <div className="flex h-16 flex-col justify-center gap-1 px-3">
        <div className="h-2 w-6 rounded bg-brand-orange/40" />
        <div className="h-1.5 w-16 rounded bg-[var(--ba-muted)]/30" />
        <div className="mt-1 space-y-1">
          <div className="h-1 w-full rounded bg-[var(--ba-muted)]/20" />
          <div className="h-1 w-3/4 rounded bg-[var(--ba-muted)]/20" />
        </div>
      </div>
    ),
  }[option.value];

  return (
    <button
      type="button"
      onClick={() => onSelect(option.value)}
      className={cn(
        'rounded-xl border p-2 text-left transition-colors',
        selected
          ? 'border-brand-orange bg-[var(--ba-accent-orange-subtle)] ring-2 ring-brand-orange/30'
          : 'border-[var(--ba-surface-border)] hover:border-brand-orange'
      )}
    >
      <div className="mb-2 overflow-hidden rounded-lg border border-[var(--ba-surface-border)] bg-black/[0.02]">
        {sketch}
      </div>
      <p className="text-sm font-semibold">{option.label}</p>
      <p className="mt-0.5 text-xs ba-muted">{option.description}</p>
    </button>
  );
}

function NewInvitation() {
  const navigate = useNavigate();
  const [form, setForm] = useState(getInitialFormValues());
  const [invitationId, setInvitationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [shareAfterSave, setShareAfterSave] = useState(null);
  const [mobileTab, setMobileTab] = useState('edit');
  const [previewViewport, setPreviewViewport] = useState('desktop');
  const [structureLocked, setStructureLocked] = useState(false);
  const [lockedBaseFields, setLockedBaseFields] = useState(null);
  const getInitialFormTab = () => {
    const id = new URLSearchParams(window.location.search).get('id');
    return id ? 'info' : 'templates';
  };
  const [activeFormTab, setActiveFormTab] = useState(getInitialFormTab);
  const [fieldsDisclaimerOpen, setFieldsDisclaimerOpen] = useState(() => {
    try {
      return localStorage.getItem(FIELDS_DISCLAIMER_STORAGE_KEY) !== 'closed';
    } catch {
      return true;
    }
  });
  const [previewFormValues, setPreviewFormValues] = useState(() => getInitialFieldValues(form.fields));

  useEffect(() => {
    setPreviewFormValues(getInitialFieldValues(form.fields));
  }, [form.fields]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id');
    const duplicateId = urlParams.get('duplicate');

    if (duplicateId) {
      setLoading(true);
      setStructureLocked(false);
      setLockedBaseFields(null);
      axios.get(`${dbUrl}invitations/${duplicateId}.json`)
        .then((res) => {
          if (res.data) {
            const normalized = normalizeInvitation(res.data);
            const withoutResponses = stripResponsesFromInvitation(normalized);
            setForm({
              ...getInitialFormValues(),
              ...withoutResponses,
              name: `${normalized.name} (copia)`,
              closesAt: normalized.closesAt || '',
              eventDate: normalized.eventDate || '',
              location: normalizeLocation(normalized.location),
              successMessage: normalized.successMessage || '',
              closedMessage: normalized.closedMessage || '',
            });
            setActiveFormTab('info');
            toast.success('Invito duplicato', 'Modifica i dettagli e salva per creare la copia.');
          } else {
            toast.error('Invito non trovato', 'L\'invito da duplicare non esiste più.');
            navigate('/');
          }
        })
        .catch(() => {
          toast.error('Errore di caricamento', 'Impossibile caricare l\'invito da duplicare.');
        })
        .finally(() => setLoading(false));
      return;
    }

    setInvitationId(id);
    if (id) setActiveFormTab('info');
    if (!id) {
      setStructureLocked(false);
      setLockedBaseFields(null);
      return;
    }

    setLoading(true);
    axios.get(`${dbUrl}invitations/${id}.json`)
      .then(async (res) => {
        if (res.data) {
          const normalized = normalizeInvitation(res.data);
          const responseCount = await getResponseCount(id, normalized.responses);
          const hasResponses = responseCount > 0;
          setStructureLocked(hasResponses);
          setLockedBaseFields(hasResponses ? ensureFieldsHaveIds(normalized.fields || []) : null);
          setForm({
            ...getInitialFormValues(),
            ...normalized,
            closesAt: normalized.closesAt || '',
            eventDate: normalized.eventDate || '',
            location: normalizeLocation(normalized.location),
            successMessage: normalized.successMessage || '',
            closedMessage: normalized.closedMessage || '',
          });
          if (hasResponses) setActiveFormTab('fields');
        } else {
          toast.error('Invito non trovato', 'L\'invito richiesto non esiste più.');
          navigate('/');
        }
      })
      .catch(() => {
        toast.error('Errore di caricamento', 'Impossibile caricare l\'invito.');
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateTheme = (key, value) => {
    setForm((prev) => ({
      ...prev,
      theme: { ...DEFAULT_THEME, ...prev.theme, [key]: value },
    }));
  };

  const applyPreset = (preset) => {
    setForm((prev) => ({
      ...prev,
      color1: preset.color1,
      color2: preset.color2,
    }));
  };

  const applyTemplateChoice = (templateId) => {
    setForm(applyTemplate(templateId));
    if (templateId === 'none') {
      setActiveFormTab('info');
    }
  };

  const closeFieldsDisclaimer = () => {
    setFieldsDisclaimerOpen(false);
    try {
      localStorage.setItem(FIELDS_DISCLAIMER_STORAGE_KEY, 'closed');
    } catch {
      // ignore storage errors
    }
  };

  const openFieldsDisclaimer = () => {
    setFieldsDisclaimerOpen(true);
    try {
      localStorage.removeItem(FIELDS_DISCLAIMER_STORAGE_KEY);
    } catch {
      // ignore storage errors
    }
  };

  const handlePreviewFieldChange = (field, value) => {
    setPreviewFormValues((prev) => pruneHiddenFieldValues({ ...prev, [field]: value }, form.fields));
  };

  const handlePreviewCheckboxChange = (field, optionValue, isChecked) => {
    setPreviewFormValues((prev) => {
      const current = Array.isArray(prev[field]) ? prev[field] : [];
      const nextValues = isChecked
        ? [...current.filter((v) => v !== optionValue), optionValue]
        : current.filter((v) => v !== optionValue);
      return pruneHiddenFieldValues({ ...prev, [field]: nextValues }, form.fields);
    });
  };

  const submitForm = async () => {
    if (!form.name?.trim()) {
      toast.error('Nome mancante', 'Inserisci un nome valido per l\'invito.');
      return;
    }

    let customId = invitationId;
    if (!invitationId) {
      customId = await resolveUniqueSlug(form.name);
      if (!customId) {
        toast.error('Nome non valido', 'Inserisci un nome valido per l\'invito.');
        return;
      }
    }

    const TMS = Date.now();

    try {
      let fieldsToSave;

      if (structureLocked && invitationId) {
        const existingRes = await axios.get(`${dbUrl}invitations/${invitationId}.json`);
        if (!existingRes.data) {
          toast.error('Invito non trovato', 'Impossibile aggiornare la visibilità dei campi.');
          setConfirmOpen(false);
          return;
        }
        const existingFields = Array.isArray(existingRes.data.fields)
          ? existingRes.data.fields
          : (lockedBaseFields || []);
        fieldsToSave = mergeFieldHiddenFlags(existingFields, form.fields);
        // PATCH only fields: a full PUT would wipe invitations/{id}/responses.
        await axios.patch(`${dbUrl}invitations/${invitationId}.json`, {
          fields: fieldsToSave,
          updatedAt: TMS,
        });
      } else {
        fieldsToSave = ensureFieldsHaveIds(form.fields);
        const formToSubmit = stripResponsesFromInvitation({
          ...form,
          fields: fieldsToSave,
          description: normalizeDescriptionForSave(form.description || form.text || ''),
          location: normalizeLocation(form.location || DEFAULT_LOCATION),
          successMessage: (form.successMessage || '').trim(),
          closedMessage: (form.closedMessage || '').trim(),
          updatedAt: TMS,
          schemaVersion: 5,
          closesAt: form.closesAt || null,
          eventDate: form.eventDate || null,
        });
        delete formToSubmit.text;

        if (invitationId) {
          // PATCH merges top-level keys and preserves the responses child node.
          await axios.patch(`${dbUrl}invitations/${customId}.json`, {
            ...formToSubmit,
            text: null,
          });
        } else {
          formToSubmit.createdAt = TMS;
          await axios.put(`${dbUrl}invitations/${customId}.json`, formToSubmit);
        }
      }

      if (structureLocked) {
        setLockedBaseFields(fieldsToSave);
        setForm((prev) => ({ ...prev, fields: fieldsToSave }));
      }
      toast.success(
        invitationId ? 'Invito aggiornato' : 'Invito creato',
        structureLocked
          ? 'La visibilità dei campi è stata aggiornata. I dati delle risposte restano invariati.'
          : invitationId
            ? 'Le modifiche sono state salvate.'
            : 'L\'invito è pronto per essere condiviso.'
      );
      setConfirmOpen(false);
      setShareAfterSave({ id: customId, name: form.name });
      setInvitationId(customId);
    } catch {
      toast.error('Salvataggio non riuscito', 'Si è verificato un errore. Riprova.');
      setConfirmOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-6xl px-4 py-6 md:px-6 lg:px-8">
        <div className="mb-6 space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardContent className="space-y-4 p-6">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="space-y-4 p-6">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-40 w-full rounded-xl" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const previewPanel = (
    <Card className="sticky top-20 overflow-hidden">
      <div className="border-b border-[var(--ba-surface-border)] px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wide text-[var(--ba-muted)]">
              Anteprima live
            </h3>
            <p className="mt-0.5 text-xs ba-muted">Come lo vedranno i partecipanti (tema chiaro)</p>
          </div>
          <div className="inline-flex rounded-lg border border-[var(--ba-surface-border)] p-0.5">
            <button
              type="button"
              className={cn(
                'inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors',
                previewViewport === 'desktop'
                  ? 'bg-[var(--ba-accent-orange-subtle)] text-brand-orange'
                  : 'text-[var(--ba-muted)] hover:text-[var(--ba-body-text)]'
              )}
              onClick={() => setPreviewViewport('desktop')}
            >
              <Monitor className="h-3.5 w-3.5" />
              Desktop
            </button>
            <button
              type="button"
              className={cn(
                'inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors',
                previewViewport === 'mobile'
                  ? 'bg-[var(--ba-accent-orange-subtle)] text-brand-orange'
                  : 'text-[var(--ba-muted)] hover:text-[var(--ba-body-text)]'
              )}
              onClick={() => setPreviewViewport('mobile')}
            >
              <Smartphone className="h-3.5 w-3.5" />
              Mobile
            </button>
          </div>
        </div>
      </div>
      <div className="max-h-[calc(100vh-180px)] overflow-y-auto">
        <InvitationCanvas invitation={form} embedded>
          <div
            className={cn(
              previewViewport === 'mobile'
                ? 'mx-auto max-w-[390px] px-3 py-6'
                : 'px-4 py-6 md:px-6'
            )}
          >
            {previewViewport === 'mobile' ? (
              <div className="overflow-hidden rounded-[1.75rem] border-[10px] border-[var(--ba-body-text)]/80 bg-[var(--ba-surface-bg)] shadow-lg">
                <div className="border-b border-[var(--ba-surface-border)] bg-black/[0.03] px-4 py-2 text-center text-[10px] font-medium text-[var(--ba-muted)]">
                  Anteprima smartphone
                </div>
                <div className="px-4 py-8">
                  <InvitationPreview
                    invitation={form}
                    mode="interactive"
                    formValues={previewFormValues}
                    onFieldChange={handlePreviewFieldChange}
                    onCheckboxChange={handlePreviewCheckboxChange}
                    onSubmit={(e) => e.preventDefault()}
                    disableSubmit
                    viewport="mobile"
                    resizeMapTrigger={previewViewport}
                  />
                </div>
              </div>
            ) : (
              <InvitationPreview
                invitation={form}
                mode="interactive"
                formValues={previewFormValues}
                onFieldChange={handlePreviewFieldChange}
                onCheckboxChange={handlePreviewCheckboxChange}
                onSubmit={(e) => e.preventDefault()}
                disableSubmit
                viewport="desktop"
                resizeMapTrigger={previewViewport}
              />
            )}
          </div>
        </InvitationCanvas>
      </div>
    </Card>
  );

  const formPanel = (
    <Tabs value={activeFormTab} onValueChange={setActiveFormTab} className="w-full min-w-0">
      <div className="mb-4 w-full min-w-0 max-w-full overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] lg:overflow-x-visible [&::-webkit-scrollbar]:hidden">
        <TabsList className="inline-flex h-auto w-max flex-nowrap justify-start gap-0.5 p-1 lg:flex lg:w-full lg:justify-center">
          {!invitationId && (
            <TabsTrigger value="templates" className="shrink-0 px-3 lg:min-w-0 lg:flex-1">
              Template
            </TabsTrigger>
          )}
          <TabsTrigger value="info" disabled={structureLocked} className="shrink-0 px-3 lg:min-w-0 lg:flex-1">Info</TabsTrigger>
          <TabsTrigger value="look" disabled={structureLocked} className="shrink-0 px-3 lg:min-w-0 lg:flex-1">Aspetto</TabsTrigger>
          <TabsTrigger value="fields" className="shrink-0 px-3 lg:min-w-0 lg:flex-1">Campi</TabsTrigger>
          <TabsTrigger value="settings" disabled={structureLocked} className="shrink-0 px-3 lg:min-w-0 lg:flex-1">Impostazioni</TabsTrigger>
        </TabsList>
      </div>

      {!invitationId && (
        <TabsContent value="templates" className="space-y-4">
          <p className="ba-muted">Scegli un template preconfigurato oppure «Nessuno» per partire da zero con un invito personalizzato.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => applyTemplateChoice('none')}
              className="rounded-xl border border-dashed border-[var(--ba-surface-border)] p-4 text-left transition-colors hover:border-brand-orange hover:bg-black/[0.02] dark:hover:bg-white/[0.02]"
            >
              <p className="font-semibold">Nessuno</p>
              <p className="mt-1 text-sm ba-muted">Invito vuoto: configura tutto da zero.</p>
            </button>
            {INVITATION_TEMPLATES.map((tpl) => (
              <button
                key={tpl.id}
                type="button"
                onClick={() => applyTemplateChoice(tpl.id)}
                className="rounded-xl border border-[var(--ba-surface-border)] p-4 text-left transition-colors hover:border-brand-orange"
              >
                <p className="font-semibold">{tpl.name}</p>
                <p className="mt-1 text-sm ba-muted">{tpl.description}</p>
              </button>
            ))}
          </div>
        </TabsContent>
      )}

      <TabsContent value="info" className="space-y-5">
        <div>
          <label className="ba-label">Nome invito</label>
          <Input required value={form.name} onChange={(e) => updateForm('name', e.target.value)} />
          <p className="mt-1 ba-muted">
            Il nome viene usato come titolo e per generare il link (/invito?id=…)
          </p>
        </div>
        <div>
          <label className="ba-label">Descrizione</label>
          <RichTextEditor
            value={form.description}
            placeholder="Inserisci una descrizione dell'invito…"
            onChange={(html) => updateForm('description', html)}
          />
          <p className="mt-1 ba-muted">
            Puoi usare grassetto, corsivo, link ed elenchi. L&apos;anteprima si aggiorna in tempo reale.
          </p>
        </div>
        <div>
          <label className="ba-label" htmlFor="event-date-picker">
            Data evento
          </label>
          <DateTimePicker
            id="event-date-picker"
            value={form.eventDate || ''}
            onChange={(v) => updateForm('eventDate', v)}
            allowOptionalTime
            placeholder="Seleziona il giorno dell'evento…"
          />
          <p className="mt-1 ba-muted">
            Opzionale. L&apos;ora è facoltativa. Visibile nell&apos;invito per i partecipanti.
          </p>
        </div>
        <LocationPicker
          value={form.location}
          onChange={(location) => updateForm('location', location)}
          accentColor={form.color1}
        />
        <ImageUpload label="Logo" hint="In assenza di logo verrà usato quello di default." value={form.logo} onChange={(v) => updateForm('logo', v)} previewMaxH={80} />
        <ImageUpload label="Immagine di copertina" hint="Mostrata in alto nell'invito pubblico." value={form.image} onChange={(v) => updateForm('image', v)} previewMaxH={120} />
      </TabsContent>

      <TabsContent value="look" className="space-y-5">
        <div>
          <label className="ba-label">Layout</label>
          <div className="mt-1 grid gap-3 sm:grid-cols-3">
            {THEME_LAYOUTS_META.map((option) => (
              <LayoutChoiceCard
                key={option.value}
                option={option}
                selected={(form.theme?.layout ?? 'classic') === option.value}
                onSelect={(value) => updateTheme('layout', value)}
              />
            ))}
          </div>
        </div>

        <div>
          <label className="ba-label">Palette rapide</label>
          <div className="flex flex-wrap gap-2">
            {COLOR_PRESETS.map((preset) => (
              <button
                key={preset.name}
                type="button"
                onClick={() => applyPreset(preset)}
                className="flex items-center gap-2 rounded-lg border border-[var(--ba-surface-border)] px-3 py-1.5 text-xs font-medium transition-colors hover:border-brand-orange"
              >
                <span className="h-4 w-4 rounded-full" style={{ backgroundColor: preset.color1 }} />
                <span className="h-4 w-4 rounded-full" style={{ backgroundColor: preset.color2 }} />
                {preset.name}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="ba-label">Colore primario</label>
            <div className="flex items-center gap-3">
              <input type="color" value={form.color1} onChange={(e) => updateForm('color1', e.target.value)} className="h-10 w-14 cursor-pointer rounded border-0" />
              <Input value={form.color1} onChange={(e) => updateForm('color1', e.target.value)} />
            </div>
          </div>
          <div>
            <label className="ba-label">Colore secondario</label>
            <div className="flex items-center gap-3">
              <input type="color" value={form.color2} onChange={(e) => updateForm('color2', e.target.value)} className="h-10 w-14 cursor-pointer rounded border-0" />
              <Input value={form.color2} onChange={(e) => updateForm('color2', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="ba-label">Stile copertina</label>
            <select className="ba-input" value={form.theme?.coverStyle ?? 'full'} onChange={(e) => updateTheme('coverStyle', e.target.value)}>
              {COVER_STYLES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="ba-label">Stile pulsante</label>
            <select className="ba-input" value={form.theme.buttonStyle} onChange={(e) => updateTheme('buttonStyle', e.target.value)}>
              {BUTTON_STYLES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="ba-label">Font</label>
            <select
              className="ba-input"
              value={form.theme?.fontFamily || 'ubuntu'}
              onChange={(e) => updateTheme('fontFamily', e.target.value)}
            >
              {FONT_FAMILIES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="ba-label">Dimensione testo</label>
            <select className="ba-input" value={form.theme.fontScale} onChange={(e) => updateTheme('fontScale', e.target.value)}>
              {FONT_SCALES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="ba-label">Testo pulsante</label>
            <Input value={form.theme.buttonText} onChange={(e) => updateTheme('buttonText', e.target.value)} placeholder="Partecipa" />
          </div>
        </div>

        <ImageUpload
          label="Sfondo invito (opzionale)"
          hint="Se impostato, verrà mostrato dietro al modulo pubblico. Senza sfondo l'invito usa sempre il tema chiaro, indipendentemente dal tema dell'app."
          value={form.theme?.backgroundImage || ''}
          onChange={(v) => updateTheme('backgroundImage', v)}
          previewMaxH={100}
        />
      </TabsContent>

      <TabsContent value="fields" className="space-y-5">
        {structureLocked && (
          <div className="rounded-xl border border-brand-orange/30 bg-brand-orange/[0.06] p-4 text-sm">
            <p className="font-semibold text-[var(--ba-body-text)]">Struttura campi bloccata</p>
            <p className="mt-1 ba-muted">
              Questo invito ha già delle risposte: puoi solo <strong className="font-medium text-[var(--ba-body-text)]">nascondere o ri-mostrare</strong> campi, sezioni e singole opzioni delle dropdown.
              Nome, tipo, etichette e regole restano invariati per preservare i dati già raccolti.
            </p>
          </div>
        )}
        {fieldsDisclaimerOpen && !structureLocked ? (
          <div className="rounded-xl border border-brand-orange/30 bg-brand-orange/[0.06] p-4 text-sm">
            <div className="flex items-start gap-3">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-brand-orange" aria-hidden />
              <div className="min-w-0 flex-1 space-y-3">
                <div>
                  <p className="font-semibold text-[var(--ba-body-text)]">Come funzionano i campi del modulo</p>
                  <p className="mt-1 ba-muted">
                    Qui definisci le domande che i partecipanti compileranno. Puoi aggiungere campi singoli
                    o raggrupparli in <strong className="font-medium text-[var(--ba-body-text)]">sezioni</strong> con
                    un titolo (es. «Dettagli volontario»).
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-[var(--ba-body-text)]">Campi e sezioni condizionali</p>
                  <p className="mt-1 ba-muted">
                    Aprendo <strong className="font-medium text-[var(--ba-body-text)]">Visibilità</strong> su un campo
                    o su una sezione puoi decidere quando mostrarlo: ad esempio, mostra «Turno preferito» solo se
                    il campo «Partecipi come» è uguale a «Volontario».
                  </p>
                  <ul className="mt-2 list-disc space-y-1 pl-5 ba-muted">
                    <li>Il campo di riferimento deve trovarsi <strong className="font-medium text-[var(--ba-body-text)]">sopra</strong> nel modulo (ordine importante).</li>
                    <li>I campi nascosti da una condizione <strong className="font-medium text-[var(--ba-body-text)]">non vengono mostrati</strong> a chi compila, non sono obbligatori e non vengono salvati nella risposta.</li>
                    <li>Se il partecipante cambia una scelta, i valori dei campi che scompaiono vengono <strong className="font-medium text-[var(--ba-body-text)]">cancellati automaticamente</strong>.</li>
                    <li>Usa l&apos;<strong className="font-medium text-[var(--ba-body-text)]">anteprima logica condizionale</strong> qui sotto per simulare le scelte e verificare cosa appare prima di pubblicare.</li>
                  </ul>
                </div>

                <p className="text-xs ba-muted">
                  Suggerimento: per flussi semplici basta un campo «scelta» seguito da una sezione condizionale con i campi collegati.
                </p>
              </div>
              <button
                type="button"
                onClick={closeFieldsDisclaimer}
                className="shrink-0 rounded-md p-1 text-[var(--ba-muted)] transition-colors hover:bg-black/5 hover:text-[var(--ba-body-text)] dark:hover:bg-white/5"
                aria-label="Chiudi guida campi"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : !structureLocked ? (
          <button
            type="button"
            onClick={openFieldsDisclaimer}
            className="flex w-full items-center gap-2 rounded-lg border border-dashed border-[var(--ba-surface-border)] px-3 py-2.5 text-left text-sm text-[var(--ba-muted)] transition-colors hover:border-brand-orange hover:text-[var(--ba-body-text)]"
          >
            <Info className="h-4 w-4 shrink-0 text-brand-orange" aria-hidden />
            <span>Mostra guida campi e visibilità condizionale</span>
            <ChevronDown className="ml-auto h-4 w-4 shrink-0 opacity-60" aria-hidden />
          </button>
        ) : null}

        <FieldBuilder
          fields={form.fields}
          onChange={(fields) => updateForm('fields', fields)}
          structureLocked={structureLocked}
        />
      </TabsContent>

      <TabsContent value="settings" className="space-y-5">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 accent-brand-orange"
            checked={form.isOpen !== false}
            onChange={(e) => updateForm('isOpen', e.target.checked)}
          />
          Invito aperto alle iscrizioni
        </label>
        <div>
          <label className="ba-label" htmlFor="closes-at-picker">
            Data chiusura iscrizioni (opzionale)
          </label>
          <DateTimePicker
            id="closes-at-picker"
            value={form.closesAt || ''}
            onChange={(v) => updateForm('closesAt', v)}
            allowOptionalTime={false}
            placeholder="Seleziona data e ora di chiusura…"
          />
          <p className="mt-1 ba-muted">Dopo questa data il modulo non accetterà nuove risposte.</p>
        </div>
        <div>
          <label className="ba-label">Messaggio dopo l&apos;invio</label>
          <textarea
            className="ba-input min-h-[80px] resize-y"
            rows={3}
            value={form.successMessage || ''}
            placeholder={DEFAULT_SUCCESS_MESSAGE}
            onChange={(e) => updateForm('successMessage', e.target.value)}
          />
          <p className="mt-1 ba-muted">Mostrato al partecipante dopo aver inviato la richiesta.</p>
        </div>
        <div>
          <label className="ba-label">Messaggio invito chiuso</label>
          <textarea
            className="ba-input min-h-[80px] resize-y"
            rows={3}
            value={form.closedMessage || ''}
            placeholder={DEFAULT_CLOSED_MESSAGE}
            onChange={(e) => updateForm('closedMessage', e.target.value)}
          />
          <p className="mt-1 ba-muted">Mostrato quando le iscrizioni sono chiuse.</p>
        </div>
      </TabsContent>
    </Tabs>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 md:py-8">
      <PageHeader
        title={invitationId ? 'Modifica invito' : 'Crea nuovo invito'}
        description={
          structureLocked
            ? 'Questo invito ha già risposte: puoi solo nascondere o ri-mostrare campi e opzioni del modulo.'
            : 'Configura il tuo invito e visualizza l\'anteprima in tempo reale.'
        }
      />

      <div className="mb-4 flex gap-2 lg:hidden">
        <Button type="button" variant={mobileTab === 'edit' ? 'default' : 'outline'} className="flex-1" onClick={() => setMobileTab('edit')}>
          Modifica
        </Button>
        <Button type="button" variant={mobileTab === 'preview' ? 'default' : 'outline'} className="flex-1" onClick={() => setMobileTab('preview')}>
          Anteprima
        </Button>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid min-w-0 gap-6 lg:grid-cols-2">
        <div className={`min-w-0 ${mobileTab === 'preview' ? 'hidden lg:block' : ''}`}>
          <Card className="min-w-0">
            <CardContent className="min-w-0 p-4 md:p-6">{formPanel}</CardContent>
          </Card>
        </div>
        <div className={`min-w-0 ${mobileTab === 'edit' ? 'hidden lg:block' : ''}`}>
          {previewPanel}
        </div>
      </motion.div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button type="button" onClick={() => setConfirmOpen(true)}>
          {structureLocked ? 'Salva visibilità campi' : invitationId ? 'Salva modifiche' : 'Crea invito'}
        </Button>
        <Button asChild variant="outline">
          <Link to="/tutti-gli-inviti">Annulla</Link>
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {structureLocked
                ? 'Conferma visibilità campi'
                : invitationId
                  ? 'Conferma modifica'
                  : 'Conferma creazione'}
            </DialogTitle>
            <DialogDescription>
              {structureLocked
                ? `Aggiornare la visibilità dei campi di "${form.name}"? Le risposte già raccolte non verranno modificate.`
                : `Sei sicuro di voler ${invitationId ? 'modificare' : 'creare'} l'invito "${form.name}"?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Annulla</Button>
            <Button onClick={submitForm}>Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ShareInvitationDialog
        open={!!shareAfterSave}
        onOpenChange={(open) => {
          if (!open) {
            setShareAfterSave(null);
            navigate('/tutti-gli-inviti');
          }
        }}
        invitationId={shareAfterSave?.id}
        invitationName={shareAfterSave?.name}
        title="Invito salvato — condividilo"
        description={
          shareAfterSave?.name
            ? `Copia il link o scarica il QR per "${shareAfterSave.name}".`
            : undefined
        }
      />
    </div>
  );
}

export default NewInvitation;
